import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ShieldCheckIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { authAPI } from '../services/api'
import { useAuthStore } from '../store/authStore'
import Spinner from './Spinner'

interface Props {
  onAccepted: () => void
}

/**
 * Shown when an authenticated user tries to use a signal feature but has not yet
 * accepted the current Terms of Service (API returns 403 with error_code "tos_required").
 */
const TosGateModal = ({ onAccepted }: Props) => {
  const [accepted, setAccepted] = useState(false)
  const [loading, setLoading] = useState(false)
  const setAuth = useAuthStore((state) => state.setAuth)
  const token = useAuthStore((state) => state.token)
  const refreshToken = useAuthStore((state) => state.refreshToken)

  const handleAccept = async () => {
    if (!accepted) {
      toast.error('Please check the box to confirm you have read and agree to the Terms')
      return
    }
    setLoading(true)
    try {
      const updatedUser = await authAPI.acceptTos()
      // Refresh the stored user profile so tos_accepted_at is up to date
      if (token) {
        setAuth(token, updatedUser, refreshToken ?? undefined)
      }
      toast.success('Terms accepted — you\'re all set')
      onAccepted()
    } catch {
      toast.error('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    // The overlay scrolls: on a short phone screen the dialog is taller than the
    // viewport, and a fixed, non-scrolling overlay left "Accept" out of reach.
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60" data-testid="tos-gate-overlay">
      <div className="flex min-h-full items-center justify-center px-4 py-6">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-8 space-y-6">
        <div className="flex items-center gap-3">
          <ShieldCheckIcon className="h-8 w-8 text-primary-600 shrink-0" />
          <h2 className="text-xl font-bold text-gray-900">Terms of Service Updated</h2>
        </div>

        <p className="text-sm text-gray-600">
          We've updated our Terms of Service to include a{' '}
          <strong>Creator Regulatory Compliance</strong> clause (Section 11). Before you can
          generate or publish trading signals, you need to acknowledge these obligations.
        </p>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 space-y-2 max-h-56 overflow-y-auto">
          <p className="font-semibold">Key obligations you are accepting:</p>
          <ul className="list-disc list-inside space-y-1.5">
            <li>
              You are solely responsible for determining whether publishing trading signals
              requires registration as an investment adviser in your jurisdiction.
            </li>
            <li>
              You must obtain any required licences or exemptions before distributing
              financial content to subscribers.
            </li>
            <li>
              You must display clear disclaimers stating that your signals are for
              informational purposes only and do not constitute personalised investment advice.
            </li>
            <li>
              You agree to indemnify FinCreator against claims arising from your Creator Content
              or failure to comply with applicable laws.
            </li>
            <li>
              FinCreator may suspend your account if we believe you are operating in violation
              of securities laws.
            </li>
          </ul>
        </div>

        <div className="flex items-start gap-3">
          <input
            id="tos-gate-check"
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
          />
          <label htmlFor="tos-gate-check" className="text-sm text-gray-700 cursor-pointer">
            I have read and agree to the updated{' '}
            <Link
              to="/terms"
              target="_blank"
              className="font-medium text-primary-600 hover:text-primary-500 underline"
            >
              Terms of Service
            </Link>{' '}
            including Section 11 (Creator Regulatory Compliance Responsibility).
          </label>
        </div>

        <button
          onClick={handleAccept}
          disabled={loading || !accepted}
          className="w-full flex justify-center items-center gap-2 py-2.5 px-4 rounded-lg text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
        >
          {loading && <Spinner size="sm" className="border-white border-t-transparent" />}
          {loading ? 'Saving…' : 'Accept and continue'}
        </button>
      </div>
      </div>
    </div>
  )
}

export default TosGateModal
